import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EnvelopeSimple, WhatsappLogo, PaperPlaneTilt } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import type { InvoiceRow, InvoiceLabels } from "./types";
import { isValidEmail, isValidPhone } from "./utils";

interface Props {
  open: boolean;
  row: InvoiceRow | null;
  labels: InvoiceLabels;
  defaultChannel?: "email" | "whatsapp";
  onClose: () => void;
  onSend: (channel: "email" | "whatsapp", target: string, note?: string) => Promise<void> | void;
}

export function InvoiceSendDialog({
  open,
  row,
  labels,
  defaultChannel = "email",
  onClose,
  onSend,
}: Props) {
  const [channel, setChannel] = React.useState<"email" | "whatsapp">(defaultChannel);
  const [target, setTarget] = React.useState("");
  const [note, setNote] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (!row) return;
    setChannel(defaultChannel);
    setTarget(defaultChannel === "email" ? row.customer.email ?? "" : row.customer.phone ?? "");
    setNote("");
    setBusy(false);
  }, [row, defaultChannel, open]);

  const valid = channel === "email" ? isValidEmail(target) : isValidPhone(target);

  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      await onSend(channel, target, note.trim() || undefined);
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-biz-surface-elevated border-biz-border sm:rounded-2xl">
        <DialogHeader>
          <DialogTitle>{labels.sendDialog.title}</DialogTitle>
          <DialogDescription className="text-biz-foreground-muted">
            {row?.invoiceNumber ?? "—"} · {row?.customer.name}
          </DialogDescription>
        </DialogHeader>

        {/* Channel tabs */}
        <div role="tablist" className="grid grid-cols-2 gap-2 p-1 bg-biz-muted rounded-biz-btn">
          {(["email", "whatsapp"] as const).map((c) => {
            const Icon = c === "email" ? EnvelopeSimple : WhatsappLogo;
            const active = channel === c;
            return (
              <button
                key={c}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => {
                  setChannel(c);
                  setTarget(c === "email" ? row?.customer.email ?? "" : row?.customer.phone ?? "");
                }}
                className={cn(
                  "h-10 inline-flex items-center justify-center gap-2 rounded-biz-btn text-sm font-semibold biz-focus-ring",
                  active
                    ? "bg-biz-surface-elevated text-biz-foreground biz-shadow-sm"
                    : "text-biz-foreground-muted hover:text-biz-foreground",
                )}
              >
                <Icon size={14} /> {labels.sendDialog[c]}
              </button>
            );
          })}
        </div>

        {/* Target */}
        <label className="flex flex-col gap-1.5 text-sm">
          <span className="font-medium text-biz-foreground">{labels.sendDialog.target}</span>
          <input
            type={channel === "email" ? "email" : "tel"}
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            placeholder={channel === "email" ? labels.sendDialog.emailPlaceholder : labels.sendDialog.phonePlaceholder}
            className="h-10 px-3 bg-biz-bg border border-biz-border rounded-biz-btn text-sm biz-focus-ring"
            aria-invalid={!valid && target.length > 0}
          />
        </label>

        {/* Note */}
        <label className="flex flex-col gap-1.5 text-sm">
          <span className="font-medium text-biz-foreground">{labels.sendDialog.messageLabel}</span>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={labels.sendDialog.messagePlaceholder}
            rows={3}
            className="px-3 py-2 bg-biz-bg border border-biz-border rounded-biz-btn text-sm biz-focus-ring resize-none"
          />
        </label>

        <DialogFooter className="gap-2 sm:gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="h-10 px-4 rounded-biz-btn border border-biz-border text-sm font-semibold hover:bg-biz-muted biz-focus-ring disabled:opacity-50"
          >
            {labels.sendDialog.cancel}
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!valid || busy}
            className="h-10 px-4 inline-flex items-center gap-1.5 rounded-biz-btn bg-biz-primary text-biz-primary-foreground text-sm font-semibold hover:bg-biz-primary-hover biz-focus-ring disabled:opacity-50"
          >
            <PaperPlaneTilt size={14} />
            {labels.sendDialog.send}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
